import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import InputForm from '../components/MemberRegistrationInputForm';
import styles from '../assets/css/member-registration.module.css';
import { registrationCheckService } from '../services/registrationCheckService';
import { convertAllSpacesInObject, convertAllSpacesInArray } from '../utils/regex';
import { memberService } from '../services/memberService';
import CustomButtons from '../components/CustomButtons';

function MemberRegistration() {
  const navigate = useNavigate();
  const location = useLocation();
  const backState = location.state;
  const getSessionStorageData = (key, fallback) => {
    try {
      const data = sessionStorage.getItem(key);
      return data ? JSON.parse(data) : fallback;
    } catch {
      return fallback;
    }
  };

  const defaultFormData = {
    name: '',
    furigana: '',
    birthdate: '1990-01-01',
    postal_code: '',
    address_resident: '',
    postal_code_post: '',
    address_post: '',
    nearest_station: '',
    phone: '',
    line_id: '',
    blood_type: '',
    emergency_name: '',
    emergency_furigana: '',
    emergency_relationship: '',
    emergency_phone: '',
    bank_name: '',
    branch_name: '',
    account_type: '',
    account_number: '',
    has_dependents: ''
  };

  // 確認画面からの戻りの場合のみbackStateを使う。それ以外はサーバーから取得
  const [formData, setFormData] = useState(
    backState?.formData || defaultFormData
  );

  const [familyMembers, setFamilyMembers] = useState(
    backState?.familyMembers || [
      { name: '', furigana: '', birthdate: '', relationship: '', cohabitation: '', occupation: '', income_range: '' }
    ]
  );

  useEffect(() => {
    // 確認画面からの戻りならサーバー取得しない（ユーザー入力を保持）
    if (backState?.formData) return;

    const fetchUserData = async () => {
      try {
        const data = await memberService.getRegistration({});
        if (data.formData) {
          setFormData(prev => {
            const serverData = {};
            Object.keys(prev).forEach(key => {
              if (data.formData[key] !== undefined && data.formData[key] !== null) {
                serverData[key] = data.formData[key];
              }
            });
            return { ...prev, ...serverData };
          });
          if (data.familyMembers && data.familyMembers.length > 0) {
            setFamilyMembers(data.familyMembers);
          }
        }
      } catch (error) {
        console.error('ユーザー情報取得エラー:', error);
      }
    };

    fetchUserData();
  }, []);

  const [errors, setErrors] = useState([]);

  useEffect(() => {
    if (errors.length > 0) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [errors]);
  
  useEffect(() => {
    const checkRegistrationStatus = async () => {
      try {
        const { 
          hasMemberData, 
          hasRouteData, 
          hasPhotoData, 
          hasConfidentialityData 
        } = await registrationCheckService.checkRegistration();
        
        if (hasConfidentialityData) {
          navigate("/");
        } else if (hasPhotoData) {
          navigate("/confidentiality-agreement");
        } else if (hasRouteData) {
          navigate("/photo-permission");
        } else if (hasMemberData) {
          navigate("/route-check");
        }
      } catch (error) {
        console.error('登録状態確認エラー:', error);
      }
    };
    
    checkRegistrationStatus();
  }, [navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFamilyChange = (index, e) => {
    const { name, value } = e.target;
    const updated = [...familyMembers];
    updated[index][name] = value;
    setFamilyMembers(updated);
  };

  const addFamilyMember = () => {
    setFamilyMembers([
      ...familyMembers,
      {
        name: '',
        furigana: '',
        birthdate: '',
        relationship: '',
        cohabitation: '',
        occupation: '',
        income_range: ''
      }
    ]);
  };

  const validateForm = () => {
    const newErrors = [];

    if (!formData.name) newErrors.push("氏名は必須です。");
    if (!formData.furigana) {
      newErrors.push("フリガナは必須です。");
    } else if (!/^[ァ-ヶー 　]+$/.test(formData.furigana)) {
      newErrors.push("フリガナは全角カタカナで入力してください。");
    }

    if (!formData.birthdate) {
      newErrors.push("生年月日は必須です。");
    } else if (new Date(formData.birthdate) >= new Date()) {
      newErrors.push("生年月日は今日より前の日付を入力してください。");
    }

    if (!formData.postal_code) {
      newErrors.push("郵便番号（住民票）は必須です。");
    } else if (!/^\d{3}-?\d{4}$/.test(formData.postal_code)) {
      newErrors.push("郵便番号（住民票）は7桁で入力してください（例: 107-0052）。");
    }
    if (!formData.address_resident) newErrors.push("住所（住民票のある）は必須です。");
    if (formData.postal_code_post && !/^\d{3}-?\d{4}$/.test(formData.postal_code_post)) {
      newErrors.push("郵便番号（郵送先）は7桁で入力してください（例: 107-0052）。");
    }
    if (!formData.nearest_station) newErrors.push("最寄駅は必須です。");

    if (!formData.phone) {
      newErrors.push("電話番号は必須です。");
    } else if (!/^0\d{9,10}$|^0\d{1,3}-\d{1,4}-\d{3,4}$/.test(formData.phone)) {
      newErrors.push("電話番号の形式が正しくありません（例: 09012345678 または 090-1234-5678）。");
    }

    if (!formData.blood_type) newErrors.push("血液型は必須です。");
    if (!formData.bank_name) newErrors.push("銀行名は必須です。");
    if (!formData.branch_name) newErrors.push("支店名は必須です。");
    if (!formData.account_type) newErrors.push("口座種別は必須です。");
    if (!formData.account_number) newErrors.push("口座番号は必須です。");

    if (!formData.emergency_name) newErrors.push("緊急連絡先の氏名は必須です。");
    if (!formData.emergency_furigana) newErrors.push("緊急連絡先のフリガナは必須です。");
    if (!formData.emergency_relationship) newErrors.push("緊急連絡先の続柄は必須です。");
    if (!formData.emergency_phone) newErrors.push("緊急連絡先の電話番号は必須です。");

    if (!formData.has_dependents) {
      newErrors.push("扶養家族の有無は必須です。");
    } else if (formData.has_dependents === '有') {
      const hasFilledMember = familyMembers.some(member => Object.values(member).some(value => value));
      if (!hasFilledMember) {
        newErrors.push("扶養家族「有」の場合は、扶養家族を1名以上入力してください。");
      }
      familyMembers.forEach((member, index) => {
        const anyInput = Object.values(member).some(value => value);
        if (anyInput) {
          if (!member.name) newErrors.push(`扶養家族 ${index + 1} の氏名は必須です。`);
          if (!member.furigana) newErrors.push(`扶養家族 ${index + 1} のフリガナは必須です。`);
          if (!member.birthdate) {
            newErrors.push(`扶養家族 ${index + 1} の生年月日は必須です。`);
          } else if (new Date(member.birthdate) >= new Date()) {
            newErrors.push(`扶養家族 ${index + 1} の生年月日は今日より前の日付を入力してください。`);
          }
          if (!member.relationship) newErrors.push(`扶養家族 ${index + 1} の続柄は必須です。`);
          if (!member.cohabitation) newErrors.push(`扶養家族 ${index + 1} の同居欄は必須です。`);
          if (!member.occupation) newErrors.push(`扶養家族 ${index + 1} の職業は必須です。`);
          if (member.relationship === '配偶者' && !member.income_range) {
            newErrors.push(`扶養家族 ${index + 1}（配偶者）の年収区分は必須です。`);
          }
        }
      });
    }

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    const sanitizedFormData = convertAllSpacesInObject(formData);
    // 「無」の場合は入力途中の扶養家族データを送らない
    const sanitizedFamilyMembers = formData.has_dependents === '無'
      ? []
      : convertAllSpacesInArray(familyMembers);
    navigate('/member-registration-confirm', {
      state: {
        formData: sanitizedFormData,
        familyMembers: sanitizedFamilyMembers
      }
    });
  };

  return (
    <div className={styles.mainContents}>
      <div className={styles.container}>
        <div className={styles["registration-card"]}>
          <h1 className={`${styles["text-primary"]} text-center mb-4`}>メンバー情報登録</h1>

          {/* エラーは上部のここだけに表示 */}
          {errors.length > 0 && (
            <div className="alert alert-danger">
              <ul className="mb-0">
                {errors.map((error, i) => <li key={i}>{error}</li>)}
              </ul>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <InputForm
              formData={formData}
              familyMembers={familyMembers}
              errors={errors} // propsは渡しておく（将来的に使うなら）
              onInputChange={handleInputChange}
              onFamilyChange={handleFamilyChange}
              onAddFamilyMember={addFamilyMember}
              onSubmit={handleSubmit}
            />

            {/* 登録ボタン */}
            <div className="d-grid mt-4">
              <CustomButtons
                rightLabel="確認画面へ"
                rightType="submit"
                onRightClick={handleSubmit}
                leftLabel=""
                leftClassName="d-none"
              />
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default MemberRegistration;
